import type { AnchorHTMLAttributes, FC, ReactNode } from 'react'

import { ArrowRight, ArrowUpRight } from 'lucide-react'
import { cn } from '@/lib/utils'

type Variant = 'ember' | 'ghost' | 'ink' | 'outline-ink'

interface ButtonLinkProps extends AnchorHTMLAttributes<HTMLAnchorElement> {
    variant?: Variant
    size?: 'md' | 'lg'
    external?: boolean
    arrow?: boolean
    children: ReactNode
}

const VARIANTS: Record<Variant, string> = {
    ember: 'bg-ember text-ink shadow-[0_14px_44px_-14px_rgba(255,91,31,0.9)] hover:bg-ember-soft',
    ghost: 'border border-white/12 bg-white/[0.03] text-bone hover:border-white/25 hover:bg-white/[0.07]',
    ink: 'bg-ink text-bone hover:bg-ink-3',
    'outline-ink': 'border border-ink/25 text-ink hover:border-ink hover:bg-ink/5'
}

const SIZES = {
    md: 'h-10 gap-1.5 rounded-xl px-4 text-sm',
    lg: 'h-12 gap-2 rounded-2xl px-5 text-[15px]'
}

const ButtonLink: FC<ButtonLinkProps> = ({
    variant = 'ember',
    size = 'lg',
    external = false,
    arrow = true,
    className,
    children,
    ...props
}) => {
    const Icon = external ? ArrowUpRight : ArrowRight
    const externalProps = external ? { target: '_blank', rel: 'noreferrer' } : {}

    return (
        <a
            className={cn(
                'group inline-flex shrink-0 items-center justify-center font-medium transition-[background-color,border-color,color,transform] duration-200 active:scale-[0.98]',
                SIZES[size],
                VARIANTS[variant],
                className
            )}
            {...externalProps}
            {...props}
        >
            {children}

            {arrow && (
                <Icon
                    aria-hidden='true'
                    className={cn('size-4 transition-transform duration-200', external ? 'group-hover:-translate-y-0.5 group-hover:translate-x-0.5' : 'group-hover:translate-x-0.5')}
                />
            )}

            {external && <span className='sr-only'> (opens in a new tab)</span>}
        </a>
    )
}

export default ButtonLink
